import { apiGet, apiPost, ApiError } from '../shared/services/apiClient';
import { randomRequestId } from '../shared/services/replayHeaders';
import { rotateClientBinding, clearClientBinding } from '../shared/services/clientBinding';

// Types for API requests and responses
export interface LoginRequest {
  username: string;
  /**
   * SHA-512 hex of the password — the primary credential (security audit #2).
   * The backend stores bcrypt(sha512hex) for migrated accounts.
   */
  passwordHash: string;
  /**
   * Raw password, RSA-OAEP encrypted with the server's public key and base64
   * encoded. Accounts still stored as bcrypt(rawPassword) can only be verified
   * with the raw password, and the client cannot tell which those are before
   * logging in — so it always sends this, and the backend unwraps it only when
   * the account needs it. Never populate this with the plaintext password.
   */
  passwordEnc?: string;
  captchaId: string;
  captchaValue: string;
}

export interface LoginPublicKeyResponse {
  success: boolean;
  keyId: string;
  publicKey: string;
}

/** SHA-512 hex digest via Web Crypto (works on https and localhost). */
export async function sha512Hex(text: string): Promise<string> {
  const buffer = await window.crypto.subtle.digest('SHA-512', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** SPKI PEM → raw DER bytes for window.crypto.subtle.importKey. */
function pemToArrayBuffer(pem: string): ArrayBuffer {
  const base64 = pem.replace(/-----[A-Z ]+-----/g, '').replace(/\s+/g, '');
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

/** Imported public key, kept for the life of the page load. */
let loginKeyCache: { keyId: string; key: CryptoKey } | null = null;

/** Drops the cached key so the next login re-fetches it (used after a rotation). */
export function clearLoginKeyCache(): void {
  loginKeyCache = null;
}

/**
 * RSA-OAEP/SHA-256 encrypt the password for the login request, so the raw value
 * never appears in the payload (security audit #2). Returns null when the key
 * cannot be fetched or imported — login then proceeds on the SHA-512 hash
 * alone, which is all a migrated account needs anyway.
 */
export async function encryptPasswordForLogin(password: string): Promise<string | null> {
  try {
    if (!loginKeyCache) {
      const response: LoginPublicKeyResponse = await apiGet('/auth/public-key');
      if (!response?.publicKey) {
        return null;
      }
      const key = await window.crypto.subtle.importKey(
        'spki',
        pemToArrayBuffer(response.publicKey),
        { name: 'RSA-OAEP', hash: 'SHA-256' },
        false,
        ['encrypt']
      );
      loginKeyCache = { keyId: response.keyId, key };
    }

    const ciphertext = await window.crypto.subtle.encrypt(
      { name: 'RSA-OAEP' },
      loginKeyCache.key,
      new TextEncoder().encode(password)
    );

    const bytes = new Uint8Array(ciphertext);
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  } catch (error) {
    console.error('Failed to encrypt password for login:', error);
    clearLoginKeyCache();
    return null;
  }
}

export interface LoginResponseData {
  id?: number;
  username?: string;
  name?: string;
  email?: string;
  role?: string;
  roleCategory?: string;
  status?: number;
}

// SECURITY: tokens are delivered ONLY via httpOnly cookies set by the backend.
// They are never present in the JSON body and must never be stored in localStorage.
export interface LoginResponse {
  success: boolean;
  error?: string;
  message?: string;
  data?: LoginResponseData;
}

export interface UserDetails {
  role: string;
  rolecategory: string;
  roleid: string;
  username: string;
}

export interface CreateCaptchaResponse {
  success: boolean;
  captchaId: string;
  captchaSvg: string;
  expiresIn?: number;
  maxAttempts?: number;
  /**
   * NOT returned by the backend — /captcha/create_captcha deliberately keeps the
   * answer server-side. Kept optional only so older callers still compile; never
   * read it, it is always undefined.
   * @deprecated
   */
  captchaValue?: string;
}

export interface VerifyCaptchaRequest {
  captchaId: string;
  captchaValue: string;
}

export interface VerifyCaptchaResponse {
  success: boolean;
  error: string;
}

export interface GetCaptchaResponse {
  success: boolean;
  captchaId: string;
  createdAt: number;
  captchaValue: string;
}

// Auth Service
class AuthService {
  /**
   * Login API
   * POST /api/auth/login
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      // Response replay (security audit #14): a fresh client binding for every
      // attempt. The session the server creates is tied to it, so a login
      // response replayed into another browser carries a session that browser
      // cannot use.
      rotateClientBinding();
      const response = await apiPost('/auth/login', credentials);

      if (response.success) {
        // Nothing is stored and the form does not move on until the server
        // confirms, from this browser, the session that response says it
        // created. A replayed or edited login response fails here.
        await this.confirmSession(response.data?.id);

        // Auth tokens live in httpOnly cookies only — nothing token-related is stored here.
        localStorage.setItem('isLoggedIn', 'true');

        // Store user details from response.data
        const userData = response.data;
        if (userData) {
          if (userData.username) {
            localStorage.setItem('username', userData.username);
          }
          if (userData.name) {
            localStorage.setItem('userName', userData.name);
          }
          if (userData.role) {
            localStorage.setItem('userRole', userData.role);
          }
          if (userData.roleCategory) {
            localStorage.setItem('userRoleCategory', userData.roleCategory);
          }
          if (userData.id) {
            localStorage.setItem('userRoleId', String(userData.id));
          }
          if (userData.email) {
            localStorage.setItem('userEmail', userData.email);
          }
        } else {
          // Fallback: store username from credentials
          localStorage.setItem('username', credentials.username);
        }
      }
      
      return response;
    } catch (error) {
      // A rejected login can simply mean the server rotated its keypair and the
      // public key we cached is stale — re-fetch it on the next attempt.
      clearLoginKeyCache();
      console.error('Login error:', error);
      throw error;
    }
  }

  /**
   * POST /api/auth/session: the server's own word on the session this browser
   * now holds (security audit #14). Throws unless it confirms a live session,
   * bound to this browser, for the user the login response named.
   */
  private async confirmSession(expectedUserId: unknown): Promise<void> {
    const session = await apiPost('/auth/session', {});
    const confirmedId = session?.data?.id;
    if (
      !session?.success ||
      confirmedId === undefined ||
      (expectedUserId !== undefined && String(confirmedId) !== String(expectedUserId))
    ) {
      throw new ApiError('Sign-in could not be confirmed. Please try again.', 401, undefined, 'SESSION_NOT_CONFIRMED');
    }
  }

  /**
   * Logout - Clear local storage
   */
  logout(): void {
    clearClientBinding(); // the session it protected is over (security audit #14)
    // Copilot conversations are stored in this browser, not just on the server.
    // Leaving them behind lets the next person to sign in on a shared machine
    // read the previous user's questions, answers, stats and SQL. Removed by
    // prefix so a future key does not silently escape this sweep.
    Object.keys(localStorage)
      .filter((key) => key.startsWith('samiksha_chat'))
      .forEach((key) => localStorage.removeItem(key));

    localStorage.removeItem('refreshToken');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('captchaId');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userRoleCategory');
    localStorage.removeItem('userRoleId');
    localStorage.removeItem('username');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    sessionStorage.clear();
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return localStorage.getItem('isLoggedIn') === 'true';
  }

  /**
   * Get user details from localStorage
   */
  getUserDetails(): UserDetails | null {
    const role = localStorage.getItem('userRole');
    const rolecategory = localStorage.getItem('userRoleCategory');
    const roleid = localStorage.getItem('userRoleId');
    const username = localStorage.getItem('username');

    if (role && rolecategory && roleid && username) {
      return {
        role,
        rolecategory,
        roleid,
        username
      };
    }

    return null;
  }

  /**
   * Get specific user property
   */
  getUserRole(): string | null {
    return localStorage.getItem('userRole');
  }

  getUserRoleCategory(): string | null {
    return localStorage.getItem('userRoleCategory');
  }

  getUserRoleId(): string | null {
    return localStorage.getItem('userRoleId');
  }

  getUsername(): string | null {
    return localStorage.getItem('username');
  }
}

// Captcha Service
class CaptchaService {
  /**
   * Create Captcha API
   * GET /captcha/create_captcha
   */
  async createCaptcha(): Promise<CreateCaptchaResponse> {
    try {
      const response = await apiGet('/captcha/create_captcha');
      
      // Store captchaId for later verification
      if (response.captchaId) {
        localStorage.setItem('captchaId', response.captchaId);
      }
      
      return response;
    } catch (error) {
      console.error('Create captcha error:', error);
      throw error;
    }
  }

  /**
   * Verify Captcha API
   * POST /captcha/verify-captcha
   */
  async verifyCaptcha(data: VerifyCaptchaRequest): Promise<VerifyCaptchaResponse> {
    try {
      const response = await apiPost('/captcha/verify-captcha', data);
      return response;
    } catch (error) {
      console.error('Verify captcha error:', error);
      throw error;
    }
  }

  /**
   * Get Captcha by ID
   * GET /captcha/get_captcha/:id
   */
  async getCaptchaById(captchaId: string): Promise<GetCaptchaResponse> {
    try {
      const response = await apiGet(`/captcha/get_captcha/${captchaId}`);
      return response;
    } catch (error) {
      console.error('Get captcha error:', error);
      throw error;
    }
  }

  /**
   * Get stored captchaId from localStorage
   */
  getStoredCaptchaId(): string | null {
    return localStorage.getItem('captchaId');
  }

  /**
   * Clear stored captchaId
   */
  clearCaptchaId(): void {
    localStorage.removeItem('captchaId');
  }
}

/**
 * Password reset / change via email OTP.
 *
 * SECURITY: the raw password never leaves the browser. Every call sends only
 * the SHA-512 hex, matching the login path. The reset token returned by
 * verifyResetOtp travels as an httpOnly cookie, so there is nothing to store
 * here between steps.
 *
 * NOTE: because the server only ever sees a 128-hex string it cannot check
 * password strength. `validatePasswordStrength` below is the ONLY enforcement
 * point — keep it in sync with whatever policy KVS agrees on.
 */
class PasswordService {
  /**
   * Idempotency flow keys (security audit #14).
   *
   * Step 3 of each flow COMMITS a password change, so it carries an
   * Idempotency-Key: if the response is lost (dropped connection, double
   * click), a retry gets the original result back instead of applying the
   * change twice.
   *
   * A flow key is minted when the OTP verifies — that is when the user enters
   * the commit phase — and the key actually sent is flowKey + a hash OVER the
   * payload. The payload half matters: the server refuses a key reused with a
   * different body (422), so a user who mistyped their current password must
   * get a NEW key on the corrected attempt, while a retry of the SAME attempt
   * must reuse the old one. Deriving the key from both gives each behaviour
   * automatically. Hashing the payload together with the random flow key keeps
   * the header value useless to anyone who sees it in transit metadata.
   */
  private resetFlowKey: string | null = null;
  private changeFlowKey: string | null = null;

  private async idempotencyKeyFor(flowKey: string, payload: unknown): Promise<string> {
    const digest = await sha512Hex(`${flowKey}:${JSON.stringify(payload)}`);
    return `${flowKey}.${digest.slice(0, 32)}`;
  }

  /** Step 1 — POST /api/auth/forgot-password/request-otp */
  async requestResetOtp(data: { username: string; captchaId: string; captchaValue: string }): Promise<OtpResponse> {
    return apiPost('/auth/forgot-password/request-otp', data);
  }

  /** Step 2 — POST /api/auth/forgot-password/verify-otp (sets the reset cookie) */
  async verifyResetOtp(data: { username: string; otp: string }): Promise<OtpResponse> {
    // The reset token this step issues is bound to the browser, like a login
    // session (security audit #14), so it needs a binding to be bound to.
    rotateClientBinding();
    const response = await apiPost('/auth/forgot-password/verify-otp', data);
    this.resetFlowKey = randomRequestId(); // commit phase begins
    return response;
  }

  /** Step 3 — POST /api/auth/forgot-password/reset */
  async resetPassword(newPassword: string): Promise<OtpResponse> {
    const newPasswordHash = await sha512Hex(newPassword);
    // Lazy fallback: a page reload between steps loses the in-memory key while
    // the httpOnly reset cookie survives — the flow still works, just with a
    // key minted now.
    const flowKey = this.resetFlowKey ?? (this.resetFlowKey = randomRequestId());
    const idempotencyKey = await this.idempotencyKeyFor(flowKey, { newPasswordHash });
    const response = await apiPost('/auth/forgot-password/reset', { newPasswordHash }, { idempotencyKey });
    this.resetFlowKey = null; // this flow is spent; the next reset is a new operation
    return response;
  }

  /** Logged-in flow, step 1 — POST /api/auth/change-password/request-otp */
  async requestChangeOtp(): Promise<OtpResponse> {
    return apiPost('/auth/change-password/request-otp', {});
  }

  /** Logged-in flow, step 2 — POST /api/auth/change-password/verify-otp */
  async verifyChangeOtp(otp: string): Promise<OtpResponse> {
    const response = await apiPost('/auth/change-password/verify-otp', { otp });
    this.changeFlowKey = randomRequestId(); // commit phase begins
    return response;
  }

  /**
   * Logged-in flow, step 3 — POST /api/auth/change-password/confirm
   * Succeeding here ends the current session, so the caller must send the user
   * back to the login screen.
   */
  async confirmChangePassword(data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<OtpResponse> {
    const [currentPasswordHash, newPasswordHash] = await Promise.all([
      sha512Hex(data.currentPassword),
      sha512Hex(data.newPassword),
    ]);
    const flowKey = this.changeFlowKey ?? (this.changeFlowKey = randomRequestId());
    const idempotencyKey = await this.idempotencyKeyFor(flowKey, { currentPasswordHash, newPasswordHash });
    const response = await apiPost(
      '/auth/change-password/confirm',
      { currentPasswordHash, newPasswordHash },
      { idempotencyKey }
    );
    this.changeFlowKey = null; // this flow is spent; the next change is a new operation
    return response;
  }
}

export interface OtpResponse {
  success: boolean;
  message?: string;
  error?: string;
  email?: string;
  expiresInMinutes?: number;
}

/**
 * Client-side password policy — the only place it is enforced (see note above).
 * Returns null when the password is acceptable, otherwise the reason why not.
 */
export function validatePasswordStrength(password: string): string | null {
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (password.length > 100) return 'Password must be under 100 characters';
  if (!/[A-Z]/.test(password)) return 'Add at least one capital letter';
  if (!/[a-z]/.test(password)) return 'Add at least one small letter';
  if (!/[0-9]/.test(password)) return 'Add at least one number';
  if (!/[^A-Za-z0-9]/.test(password)) return 'Add at least one special character';
  return null;
}

// Export service instances
export const authService = new AuthService();
export const captchaService = new CaptchaService();
export const passwordService = new PasswordService();

// Export for default import
export default {
  auth: authService,
  captcha: captchaService,
  password: passwordService
};

